import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CreditCard, Paperclip, ShoppingCart, Truck } from 'lucide-react';
import { PAYMENT_PROOF_CONTENT_TYPES, PAYMENT_PROOF_MAX_BYTES } from '@his/shared';
import { publicPost } from '@/lib/public-api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { EmptyState } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { usePublicAuth } from './PublicLayout';
import { usePrefill } from './use-prefill';
import { cartTotal, clearCartDraft, readCartDraft } from './cart-draft';

export function CheckoutPage() {
  const { slug = '', branchId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('portal');
  const { account, requireSignIn } = usePublicAuth();
  const returnTo = safeReturnTo(params.get('returnTo'), `/find/${slug}/${branchId}?tab=pharmacy`);
  const [lines] = useState(() => readCartDraft(slug, branchId));
  const total = cartTotal(lines);
  const needsPrescription = lines.some((line) => line.product.isRxRequired);

  const [customerName, setCustomerName] = useState(account?.name ?? '');
  const [phone, setPhone] = useState(account?.phone ?? '');
  const [address, setAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cod' | 'transfer'>('cod');
  const [proof, setProof] = useState<{ key: string; name: string } | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofError, setProofError] = useState<string | null>(null);
  const [paymentRef, setPaymentRef] = useState('');
  const [prescriptionUrl, setPrescriptionUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  usePrefill(customerName, setCustomerName, account?.name);
  usePrefill(phone, setPhone, account?.phone);

  const uploadProof = async (file: File) => {
    setProofError(null);
    if (!(PAYMENT_PROOF_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      setProofError(t('checkout.proof_wrong_type'));
      return;
    }
    if (file.size > PAYMENT_PROOF_MAX_BYTES) {
      setProofError(t('checkout.proof_too_big'));
      return;
    }
    setUploadingProof(true);
    try {
      const slot = await publicPost<{ key: string; url: string; method: string; headers?: Record<string, string> }>('/orders/payment-proof', {
        tenantSlug: slug,
        branchId,
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const sent = await fetch(slot.url, { method: slot.method || 'PUT', headers: slot.headers ?? { 'Content-Type': file.type }, body: file });
      if (!sent.ok) throw new Error(String(sent.status));
      setProof({ key: slot.key, name: file.name });
    } catch (caught) {
      setProof(null);
      setProofError(caught instanceof Error && caught.message && !/^\d+$/.test(caught.message) ? describeError(caught) : t('checkout.proof_failed'));
    } finally {
      setUploadingProof(false);
    }
  };

  const place = useMutation({
    mutationFn: () =>
      publicPost<{ number: string }>('/orders', {
        tenantSlug: slug,
        branchId,
        customerName,
        phone,
        address,
        paymentMethod,
        paymentProofKey: paymentMethod === 'transfer' ? proof?.key : undefined,
        paymentRef: paymentMethod === 'transfer' ? paymentRef.trim() || undefined : undefined,
        prescriptionUrl: prescriptionUrl.trim() || undefined,
        notes: notes.trim() || undefined,
        items: lines.map((line) => ({ productId: line.product.id, qty: line.qty })),
      }),
    onSuccess: (result) => {
      toast.success(`Order ${result.number} placed`, 'The pharmacy will verify and confirm it.');
      clearCartDraft(slug, branchId);
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (lines.length === 0) {
    return (
      <EmptyState
        title="Your basket is empty"
        description="Add medicines from the pharmacy list first."
        action={<Button onClick={() => navigate(returnTo)}>Back to the pharmacy</Button>}
      />
    );
  }

  const ready = Boolean(
    customerName.trim() && phone.trim() && address.trim().length >= 5 && !uploadingProof && (paymentMethod === 'cod' || Boolean(proof)),
  );
  const dirty =
    !place.isSuccess &&
    (address !== '' ||
      paymentMethod !== 'cod' ||
      proof !== null ||
      paymentRef !== '' ||
      prescriptionUrl !== '' ||
      notes !== '' ||
      customerName !== (account?.name ?? '') ||
      phone !== (account?.phone ?? ''));

  return (
    <FormScreen
      title="Place your order"
      description="The pharmacy checks the order and confirms it before dispensing"
      icon={ShoppingCart}
      eyebrow={t('screen.find_care')}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!account) {
          requireSignIn();
          return;
        }
        if (!ready) return;
        setError(null);
        place.mutate();
      }}
    >
      <FormSection title={t('section.order')} icon={ShoppingCart} columns={1}>
        <ul className="rounded-md border p-3 text-sm">
          {lines.map((line) => (
            <li key={line.product.id} className="flex justify-between py-0.5">
              <span>
                {line.product.name} × {line.qty}
              </span>
              <span className="tabular">{money(line.product.priceMinor * line.qty)}</span>
            </li>
          ))}
          <li className="mt-1 flex justify-between border-t pt-1 font-medium">
            <span>Total</span>
            <span className="tabular">{money(total)}</span>
          </li>
        </ul>
      </FormSection>

      <FormSection title={t('section.delivery')} icon={Truck}>
        <Field label="Your name" required>
          <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        </Field>
        <Field label="Phone" required>
          <PhoneText value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
        </Field>
        <Field label="Delivery address" required className="sm:col-span-2">
          <Textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        {needsPrescription && (
          <Field
            label="Link to your prescription"
            className="sm:col-span-2"
            hint="One of these medicines needs a prescription; the pharmacy will ask for it otherwise"
          >
            <Input value={prescriptionUrl} onChange={(e) => setPrescriptionUrl(e.target.value)} />
          </Field>
        )}
        <Field label="Anything the pharmacy should know" className="sm:col-span-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={t('section.payment')} icon={CreditCard}>
        <Field label="Payment" className="sm:col-span-2">
          <ChoiceButtons
            value={paymentMethod}
            onChange={setPaymentMethod}
            options={[
              { value: 'cod', label: 'Cash on delivery' },
              { value: 'transfer', label: 'Bank or wallet transfer' },
            ]}
          />
        </Field>
        {paymentMethod === 'transfer' && (
          <>
            <Field label={t('checkout.proof_label')} required hint={t('checkout.proof_hint')} error={proofError ?? undefined}>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted focus-within:ring-2 focus-within:ring-ring">
                  <Paperclip className="h-4 w-4" aria-hidden />
                  {uploadingProof ? t('checkout.proof_uploading') : proof ? t('checkout.proof_replace') : t('checkout.proof_choose')}
                  <input
                    type="file"
                    className="sr-only"
                    accept={PAYMENT_PROOF_CONTENT_TYPES.join(',')}
                    disabled={uploadingProof}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void uploadProof(file);
                    }}
                  />
                </label>
                {proof && <span className="min-w-0 truncate text-sm text-muted-foreground">{t('checkout.proof_attached', { name: proof.name })}</span>}
                <input type="hidden" data-field-value value={proof?.key ?? ''} />
              </div>
            </Field>
            <Field label="Transaction reference">
              <Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} />
            </Field>
          </>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={account ? 'Place order' : 'Sign in to continue'}
        loading={place.isPending}
        incomplete={Boolean(account) && !ready}
        cancelTo={returnTo}
        bleed={false}
      />
    </FormScreen>
  );
}
